import { useEffect, useMemo } from "react"
import { Button, CharField } from "@inmagik/magik-ui"
import { Modal, Table } from "react-bootstrap"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import type { FiltersBase, Media } from "../../../types"
import { useQsFilters } from "../../../hooks/filters"
import { useCreateMedia, useOnlyMediaResults } from "../../../hooks/media"
import { CustomPagination } from "../../../components/CustomPagination"
import MediaModal from "../../Media/MediaModal"


const initFilters = (params: URLSearchParams) =>
  ({
    page: Number(params.get("page") ?? 1),
    search: params.get("search") ?? "",
    ordering: params.get("ordering") ?? "pk",
  }) as FiltersBase

type MediaSelectionModalProps = {
  buttonLabel?: string
  title?: string
  note?: string
  selectionSummary?: string
  filterMedia?: (doc: Media) => boolean
  renderSelectionCell: (doc: Media) => React.ReactNode
  onCreateAndSelect?: (doc: Media) => void
  size?: "lg" | "xl"
}

export function MediaSelectionModal({
  buttonLabel = "Seleziona media",
  title = "Seleziona media",
  note,
  selectionSummary,
  filterMedia,
  renderSelectionCell,
  onCreateAndSelect,
  size = "xl",
}: MediaSelectionModalProps) {
  const { filters, uiFilters, setFilters } = useQsFilters(initFilters)
  const { results, numPages } = useOnlyMediaResults(filters)
  const createMedia = useCreateMedia()

  const [stateSelectionModal, actionsSelectionModal] = useModalTrigger()
  const [stateCreateNewMedia, actionsCreateNewMedia] = useModalTrigger()

  useEffect(() => {
    const el = document.getElementById("note-block-modal")
    if (!el) return
    if (selectionSummary) {
      el.classList.add("text-muted")
      el.textContent = selectionSummary
    } else {
      el.classList.remove("text-muted")
      el.textContent = ""
    }
  }, [selectionSummary])

  const items = useMemo(
    () => (filterMedia ? results.filter(filterMedia) : results),
    [filterMedia, results],
  )

  return (
    <>
      <Button size="lg" title={buttonLabel} onClick={() => actionsSelectionModal.open()} />

      <Modal show={stateSelectionModal.isOpen} onHide={actionsSelectionModal.close} size={size} centered>
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div style={{ maxHeight: 520, overflowY: "auto", position: "relative", minHeight: 420 }}>
            <div
              style={{
                position: "sticky",
                top: 0,
                backgroundColor: "white",
                zIndex: 2,
                paddingBottom: 8,
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              <CharField
                label="Cerca immagini da associare"
                placeholder="Cerca..."
                leadingIcon="search"
                style={{ width: "500px" }}
                value={uiFilters.search}
                onChange={(v) => setFilters({ ...filters, search: v, page: 1 })}
              />
              <Button className="mt-3" size="md" title="Aggiungi media" onClick={() => actionsCreateNewMedia.open()} />
            </div>

            {note && (
              <div className="mt-3 mb-2 text-muted" style={{ fontSize: 13 }}>
                {note}
              </div>
            )}

            <Table hover responsive className="mt-3">
              <thead style={{ position: "sticky", top: 0, backgroundColor: "white", zIndex: 1 }}>
                <tr>
                  <th style={{ width: 160 }}>Seleziona</th>
                  <th style={{ width: "55%" }}>Titolo</th>
                  <th>Preview</th>
                </tr>
              </thead>
              <tbody>
                {items.map((doc: Media) => (
                  <tr key={doc.id}>
                    <td>{renderSelectionCell(doc)}</td>
                    <td>{doc.title}</td>
                    <td>
                      {doc.thumbnail ? (
                        <img
                          src={String(doc.thumbnail)}
                          alt={doc.title || "Media preview"}
                          style={{ maxHeight: 80, maxWidth: 120, objectFit: "cover" }}
                        />
                      ) : doc.media_type === "VIDEO" ? (
                        <video
                          src={String(doc.media_file ?? "")}
                          style={{ maxHeight: 80, maxWidth: 120, objectFit: "cover" }}
                          controls={false}
                        />
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Modal.Body>

        <Modal.Footer className="d-flex justify-content-between align-items-center">
          <div className="text-muted" style={{ fontSize: 13 }}>{selectionSummary || ""}</div>
          <div className="d-flex align-items-center gap-2">
            <CustomPagination
              page={filters.page}
              numPages={numPages}
              onPageChange={(page) => setFilters({ ...filters, page })}
            />
            <Button variant="secondary" outline title="Chiudi" onClick={() => actionsSelectionModal.close()} />
          </div>
        </Modal.Footer>
      </Modal>

      {stateCreateNewMedia.isOpen && (
        <MediaModal
          show={stateCreateNewMedia.isOpen}
          onClose={actionsCreateNewMedia.close}
          title="Aggiungi nuovo media"
          save={async (data) => {
            const newDoc = await createMedia.mutateAsync(data as Media)
            onCreateAndSelect?.(newDoc)
            actionsCreateNewMedia.close()
          }}
        />
      )}
    </>
  )
}
